import { useCallback, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import BugReportIcon from '@mui/icons-material/BugReport';
import AddIcon from '@mui/icons-material/Add';
import { bugsApi } from '../../api/acceptanceApi';
import { getApiErrorMessage } from '../../api/client';
import { Can } from '../../auth/Can';
import { formatDateTime } from '../../utils/date';
import { BUG_STATUS_META, isBugOpen } from './statusMeta';
import { BUG_SEVERITY_META, BUG_SEVERITY_OPTIONS } from './severityMeta';
import { BUG_SOURCE_COLOR, BUG_SOURCE_OPTIONS, bugSourceLabel } from './sourceMeta';
import { BugFormDialog } from './BugFormDialog';
import { BugDetailDialog } from './BugDetailDialog';
import { useProjects } from '../../context/ProjectContext';
import type { Bug, BugSeverity, BugSource } from '../../types';

interface FeatureBugsSectionProps {
  featureId: string;
  featureName: string;
  /** Gọi khi danh sách bug thay đổi — để cha tải lại trạng thái Fixing của tính năng. */
  onChanged?: () => void;
}

/** Khối "Lỗi (bug)" trong màn chi tiết tính năng: danh sách + nút thêm bug. */
export function FeatureBugsSection({ featureId, featureName, onChanged }: FeatureBugsSectionProps): JSX.Element {
  const { projects } = useProjects();
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Bug | null>(null);
  const [severityFilter, setSeverityFilter] = useState<BugSeverity | ''>('');
  const [sourceFilter, setSourceFilter] = useState<BugSource | ''>('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await bugsApi.list({
        feature_id: featureId,
        limit: 200,
        ...(sourceFilter ? { source: sourceFilter } : {}),
      });
      setBugs(page.items);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể tải danh sách lỗi.'));
      setBugs([]);
    } finally {
      setLoading(false);
    }
  }, [featureId, sourceFilter]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const handleChanged = () => {
    void load();
    onChanged?.();
  };

  const openCount = bugs.filter((b) => isBugOpen(b.status)).length;
  const visibleBugs = severityFilter ? bugs.filter((b) => b.severity === severityFilter) : bugs;
  // Toàn bộ bug của 1 feature thuộc cùng 1 dự án — lấy project_id từ bug đầu tiên là đủ.
  const customerName = projects.find((p) => p.id === bugs[0]?.project_id)?.customer_name;

  return (
    <Paper variant="outlined" sx={{ p: 3 }} data-testid="feature-bugs-section">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BugReportIcon fontSize="small" color={openCount > 0 ? 'error' : 'action'} />
          <Typography variant="subtitle1" fontWeight={700}>
            Lỗi (bug)
          </Typography>
          {openCount > 0 && <Chip label={`${openCount} đang mở`} color="error" size="small" />}
        </Stack>
        <Can permission="BUG_CREATE">
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Thêm bug
          </Button>
        </Can>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField
          select
          size="small"
          label="Độ nghiêm trọng"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as BugSeverity | '')}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Tất cả</MenuItem>
          {BUG_SEVERITY_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {BUG_SEVERITY_META[s].label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Nguồn"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as BugSource | '')}
          sx={{ minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Tất cả</MenuItem>
          {BUG_SOURCE_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {bugSourceLabel(s, customerName)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} />
        </Stack>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : visibleBugs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Chưa ghi nhận lỗi nào cho tính năng này.
        </Typography>
      ) : (
        <List dense disablePadding>
          {visibleBugs.map((bug) => {
            const meta = BUG_STATUS_META[bug.status];
            return (
              <ListItemButton key={bug.id} onClick={() => setSelected(bug)} sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary={bug.title}
                  secondary={`${bug.created_by_name ?? 'Không rõ'} · ${formatDateTime(bug.created_at)}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                  <Chip
                    label={bugSourceLabel(
                      bug.source,
                      projects.find((p) => p.id === bug.project_id)?.customer_name,
                    )}
                    color={BUG_SOURCE_COLOR[bug.source] === 'default' ? 'default' : BUG_SOURCE_COLOR[bug.source]}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={meta.label}
                    color={meta.color === 'default' ? 'default' : meta.color}
                    size="small"
                    variant={meta.color === 'default' ? 'outlined' : 'filled'}
                  />
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      )}

      <BugFormDialog
        open={createOpen}
        featureId={featureId}
        featureName={featureName}
        bug={null}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          handleChanged();
        }}
      />

      {selected && (
        <BugDetailDialog
          bug={selected}
          hideFeatureLink
          onClose={() => setSelected(null)}
          onChanged={handleChanged}
        />
      )}
    </Paper>
  );
}
